import { useState, useMemo } from 'react'

import { useAuth } from 'context/authentication/useAuth'
import { featureFlagService } from 'utils/featureFlagService'
import { Tabs } from 'components/FbUI/Tabs'

import {
  AIGeneratedSequence,
  Sequence,
  useDeleteSequence,
  useGetCampaignSequence,
} from '../sequences_api'
import { useGetCampaignSettings } from '../campaign_settings_api'
import { SequenceEmptyState } from './SequenceEmptyState'
import { SequenceDisplay } from './SequenceDisplay'
import { SequenceSettings } from './SequenceSettings'
import { SequenceSchedule } from './SequenceSchedule'
import { CreateSequenceModal } from './CreateSequenceModal'
import { EditSequenceModal } from './EditSequenceModal'
import { AISequencePromptModal } from './AISequencePromptModal'
import { LeadsTab } from './LeadsTab'
import { SenderEmailsTab } from './SenderEmailsTab'
import { LaunchTab } from './LaunchTab'
import { ScheduledEmailsTab } from './ScheduledEmailsTab'
import { RepliesTab } from './RepliesTab'

type SequenceSubTab =
  | 'steps'
  | 'settings'
  | 'schedule'
  | 'leads'
  | 'sender-emails'
  | 'launch'
  | 'scheduled-emails'
  | 'replies'

// Campaign statuses that indicate the campaign has been launched at some point
const LAUNCHED_STATUSES = [
  'launching',
  'queued',
  'active',
  'paused',
  'completed',
]

/**
 * Hook to determine if the Sequence tab should be visible.
 * Returns true if:
 * - Company has bison_workspace
 * - Feature flag enable_email_sequencing is enabled
 */
export function useSequenceTabVisible(): boolean {
  const { user } = useAuth()
  const { enableEmailSequencing } = featureFlagService()

  const hasBisonWorkspace = user?.company?.has_bison_workspace ?? false

  return hasBisonWorkspace && enableEmailSequencing
}

interface SequenceTabProps {
  campaignId: number
}

// Base tabs always shown
const BASE_TABS: { key: SequenceSubTab; title: string }[] = [
  { key: 'steps', title: 'Steps' },
  { key: 'settings', title: 'Settings' },
  { key: 'schedule', title: 'Schedule' },
  { key: 'leads', title: 'Leads' },
  { key: 'sender-emails', title: 'Sender Emails' },
  { key: 'launch', title: 'Launch' },
]

// Tab shown only when campaign is launched (scheduled emails are passthrough API)
const SCHEDULED_EMAILS_TAB = {
  key: 'scheduled-emails' as const,
  title: 'Scheduled Emails',
}

// Tab shown only when campaign is launched (replies are stored locally, synced via webhooks)
const REPLIES_TAB = {
  key: 'replies' as const,
  title: 'Replies',
}

export function SequenceTab({ campaignId }: SequenceTabProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiGeneratedSequence, setAIGeneratedSequence] =
    useState<AIGeneratedSequence | null>(null)
  const [editingSequence, setEditingSequence] = useState<Sequence | null>(null)
  const [selectedSubTab, setSelectedSubTab] = useState<SequenceSubTab>('steps')

  const { data: sequence, isLoading } = useGetCampaignSequence(campaignId)
  const { data: settings } = useGetCampaignSettings(campaignId)
  const deleteSequenceMutation = useDeleteSequence(campaignId)

  // Determine if campaign has been launched (show scheduled emails tab)
  const isLaunched = LAUNCHED_STATUSES.includes(settings?.status || '')

  // Build tabs dynamically based on campaign status
  const subTabs = useMemo(() => {
    if (isLaunched) {
      // Add scheduled-emails and replies tabs after launch tab
      return [...BASE_TABS, SCHEDULED_EMAILS_TAB, REPLIES_TAB]
    }
    return BASE_TABS
  }, [isLaunched])

  const handleCreateSequence = () => {
    setAIGeneratedSequence(null) // Clear any previous AI generation
    setShowCreateModal(true)
  }

  const handleCreateWithAI = () => {
    setShowAIModal(true)
  }

  const handleAIGenerated = (generated: AIGeneratedSequence) => {
    setAIGeneratedSequence(generated)
    setShowAIModal(false)
    setShowCreateModal(true) // Open create modal with AI-generated values
  }

  const handleCreateModalClose = () => {
    setShowCreateModal(false)
    setAIGeneratedSequence(null) // Clear AI generation when closing
  }

  const handleEditSequence = () => {
    if (sequence) {
      setEditingSequence(sequence)
    }
  }

  const handleDeleteSequence = async () => {
    if (sequence) {
      await deleteSequenceMutation.mutateAsync(sequence.id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  const renderContent = () => {
    if (selectedSubTab === 'steps') {
      return (
        <SequenceDisplay
          sequence={sequence!}
          onEdit={handleEditSequence}
          onDelete={handleDeleteSequence}
          isDeleting={deleteSequenceMutation.isPending}
        />
      )
    }

    if (selectedSubTab === 'schedule') {
      return <SequenceSchedule campaignId={campaignId} />
    }

    if (selectedSubTab === 'leads') {
      return <LeadsTab campaignId={campaignId} />
    }

    if (selectedSubTab === 'sender-emails') {
      return <SenderEmailsTab campaignId={campaignId} />
    }

    if (selectedSubTab === 'launch') {
      return (
        <LaunchTab
          campaignId={campaignId}
          bisonCampaignId={sequence!.bison_campaign_id}
          sequence={sequence ?? null}
          onNavigateToTab={setSelectedSubTab}
        />
      )
    }

    if (selectedSubTab === 'scheduled-emails') {
      return <ScheduledEmailsTab campaignId={campaignId} />
    }

    if (selectedSubTab === 'replies') {
      return <RepliesTab campaignId={campaignId} />
    }

    return <SequenceSettings campaignId={campaignId} />
  }

  return (
    <div className="h-full w-full p-6 flex flex-col">
      {!sequence ? (
        <SequenceEmptyState
          onCreateSequence={handleCreateSequence}
          onCreateWithAI={handleCreateWithAI}
        />
      ) : (
        <>
          {/* Header */}
          <div>
            <h2 className="text-lg font-semibold text-fg">{sequence.title}</h2>
            <p className="text-sm text-fg-secondary mb-2">
              {sequence.steps.length} step
              {sequence.steps.length !== 1 ? 's' : ''}
            </p>
            <Tabs
              tabs={subTabs}
              selectedTab={selectedSubTab}
              setSelectedTab={setSelectedSubTab}
              isSmall
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden pt-4">{renderContent()}</div>
        </>
      )}

      {/* Modals - rendered once regardless of sequence state */}
      <AISequencePromptModal
        campaignId={campaignId}
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerated={handleAIGenerated}
      />

      <CreateSequenceModal
        campaignId={campaignId}
        isOpen={showCreateModal}
        onClose={handleCreateModalClose}
        initialValues={aiGeneratedSequence}
      />

      <EditSequenceModal
        sequence={editingSequence}
        campaignId={campaignId}
        isOpen={!!editingSequence}
        onClose={() => setEditingSequence(null)}
      />
    </div>
  )
}
